import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
import { LoggedContext } from '../Context';
import { getMobilePlatform, MOBILE_APP_SCHEME, STORE_URLS } from '../helpers/mobileApp';
import { verifyMagicToken } from '../redux/magicLinkService';
import { profilRedux } from '../redux/profilService';
import { capture, identifyUser } from '../services/analytics';

/**
 * Landing page for magic links — `/magic?token=…`.
 *
 * On a phone, the mobile app is the default target: when it is installed, the
 * OS opens it directly (Universal Link / App Link) and this page never
 * renders. The page only runs as a fallback:
 *
 * - Mobile without the app → redirect to the store (with the token preserved
 *   so a store-install deferred-deep-link flow could pick it up later).
 * - Desktop → redeem the token in-browser (POST /api/magic/verify) and log
 *   the user into the WEB app directly, mirroring the password login flow.
 */
const MagicLanding = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const platform = getMobilePlatform();
  const redirecting = Boolean(platform);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loggedContext = useContext(LoggedContext);
  // 'verifying' | 'error' — only meaningful on desktop (no mobile platform).
  const [status, setStatus] = useState(token ? 'verifying' : 'error');
  // Tokens are single-use: never fire the redeem call twice (re-renders,
  // dev double-effects).
  const redeemStartedRef = useRef(false);
  const appOpenStartedRef = useRef(false);

  // A Universal/App Link cannot re-open the app when Safari/Chrome is already
  // browsing neoevent.app. Try the app's custom scheme instead. If the app is
  // installed, the operating system transfers the full magic link to it; the
  // platform-native banner and store fallback remain available otherwise.
  useEffect(() => {
    if (!platform || appOpenStartedRef.current) return undefined;
    appOpenStartedRef.current = true;

    const appUrl = token
      ? `${MOBILE_APP_SCHEME}?token=${encodeURIComponent(token)}`
      : MOBILE_APP_SCHEME;
    const openTimer = window.setTimeout(() => {
      capture('magic_link_app_open_attempted', { platform });
      window.location.href = appUrl;
    }, 100);

    return () => window.clearTimeout(openTimer);
  }, [platform, token]);

  // iOS: drive the native Smart App Banner. Its "OPEN" hands the app `app-argument`
  // — the full magic URL (token included) — which is the reliable way to open the
  // app from this same-domain page (same-domain Universal Links don't fire on tap).
  // The site-wide meta tag is temporarily enriched so the current token
  // travels with the banner's OPEN action without creating a duplicate tag.
  useEffect(() => {
    if (platform !== 'ios') return undefined;
    let meta = document.querySelector('meta[name="apple-itunes-app"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'apple-itunes-app';
      document.head.appendChild(meta);
    }
    const previousContent = meta.content;
    // app-id = App Store id for Neo Network (mirrors STORE_URLS.ios).
    meta.content = `app-id=6450933172, app-argument=${window.location.href}`;
    return () => {
      if (created && meta.parentNode) {
        meta.parentNode.removeChild(meta);
      } else {
        meta.content = previousContent;
      }
    };
  }, [platform, token]);

  // Android has no native app banner: if this page renders, the App Link didn't
  // open the app, so fall back to the Play Store. (iOS relies on the Smart App
  // Banner above instead of a forced redirect.)
  useEffect(() => {
    if (platform !== 'android') return undefined;
    if (!token) return undefined;
    const timer = setTimeout(() => {
      capture('magic_link_store_redirected', { platform: 'android' });
      window.location.href = STORE_URLS.android;
    }, 1200);
    return () => clearTimeout(timer);
  }, [platform, token]);

  // A tokenless /magic URL is the Universal/App Link used by static QR codes.
  // Installed apps intercept it before the browser loads this page. Reaching
  // the web fallback therefore means the link was not handled by the app, so
  // continue immediately to the Neo Event website.
  useEffect(() => {
    if (token) return undefined;
    window.location.replace('/');
    return undefined;
  }, [token]);

  // Desktop: redeem the token and open a logged-in web session (same dispatch
  // chain as Login.js, so downstream state is indistinguishable).
  useEffect(() => {
    if (platform || !token || redeemStartedRef.current) return;
    redeemStartedRef.current = true;
    verifyMagicToken(token, 'web')
      .then(async reponse => {
        await dispatch({ type: 'user/addRefresh', payload: reponse.data.refresh });
        await dispatch({ type: 'user/addAccess', payload: reponse.data.access });
        const participantId = jwt_decode(reponse.data.access).participant_id;
        await dispatch({ type: 'user/addId', payload: participantId });
        identifyUser(participantId);
        capture('login_success', { method: 'magic_link_web' });
        await dispatch({ type: 'user/addErreur', payload: false });
        await profilRedux(participantId, reponse.data.access).then(response => {
          dispatch({ type: 'Profil/addProfil', payload: response.data });
        });
        loggedContext.setLogged(true);
        navigate('/', { replace: true });
      })
      .catch(erreur => {
        const reason = erreur.response?.data?.error;
        capture('magic_link_web_failed', {
          status: erreur.response?.status,
          reason: erreur.response ? reason || 'invalid_or_expired' : 'network_error',
        });
        setStatus(reason === 'already_used' ? 'already_used' : 'error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, token]);

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <h1 style={styles.title}>Neo Network</h1>
        {redirecting ? (
          <>
            <p style={styles.text}>Ouverture de l’application…</p>
            <p style={styles.hint}>
              Si rien ne se passe, installez l’application puis rouvrez le lien.
            </p>
            <a
              style={styles.btn}
              href={STORE_URLS[platform]}
              onClick={() => capture('magic_link_store_download_clicked', { platform })}
            >
              Télécharger l’application
            </a>
          </>
        ) : status === 'verifying' ? (
          <p style={styles.text}>Connexion en cours…</p>
        ) : (
          <>
            <p style={styles.text}>
              {token
                ? status === 'already_used'
                  ? 'Ce lien de connexion a déjà été utilisé.'
                  : 'Ce lien de connexion est invalide ou a expiré.'
                : 'Lien invalide ou expiré (aucun jeton).'}
            </p>
            <a style={styles.btn} href="/login">
              Se connecter
            </a>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  wrap: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    background: '#f2f3f5',
  },
  card: {
    maxWidth: 420,
    width: '100%',
    background: '#fff',
    borderRadius: 12,
    padding: '28px 22px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  },
  title: { fontSize: 22, fontWeight: 600, margin: '0 0 12px', color: '#134491' },
  text: { fontSize: 15, color: '#333', margin: '0 0 8px', lineHeight: 1.5 },
  hint: { fontSize: 13, color: '#888', margin: '8px 0 0' },
  stores: { display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 },
  btn: {
    display: 'inline-block',
    marginTop: 16,
    padding: '10px 18px',
    borderRadius: 22,
    background: '#134491',
    color: '#fff',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
  },
};

export default MagicLanding;
